import React, { useCallback, useEffect, useMemo } from "react";
import { Box, Stack, Switch, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WebIcon from "@mui/icons-material/Web";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TagIcon from "@mui/icons-material/Tag";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  PlatformMapping,
  useGetPlatformCandidatesMutation,
  useGetPlatformMappingsQuery,
  useUpdateAssociatedCampaignMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import { useUserRole } from "../../hooks/useUserRole";
import { resolveBriefingPlatformIcon } from "../../utils/iconUtils";
import { CandidateSelector } from "./CandidateSelector";
import SectionCard from "./inline/SectionCard";
import FieldGroup from "./inline/FieldGroup";
import InlineField from "./inline/InlineField";
import { InlineSelect, InlineText } from "./inline/inputs";
import {
  bindField,
  FieldErrors,
  useInlineSection,
} from "./inline/useInlineSection";
import { extractApiErrorMessage } from "./inline/apiErrors";

type AssociatedCampaign = components["schemas"]["AssociatedCampaignResponse"];
type Briefing = components["schemas"]["BriefingDetailResponse"];
type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type BriefingCampaignType = components["schemas"]["BriefingCampaignType"];
type EntityType = components["schemas"]["EntityType"];
type Funnel = components["schemas"]["_36_Enums.BriefingFunnel"];
type NativeAdProvider = "TABOOLA" | "OUTBRAIN";
type InvestmentPayment = "CLIENTE" | "TI_MEDIA_GROUP_SL";

const FUNNELS: Funnel[] = ["AWARENESS", "CONSIDERATION", "PERFORMANCE"];

interface InfoDraft {
  platform: BriefingPlatform;
  briefingCampaignType: BriefingCampaignType | "";
  funnel: Funnel | "";
  campaignLevel: EntityType;
  name: string;
  subcampaignName: string;
  sourceId: string;
  advertiserId: string;
  nativeAdProvider: NativeAdProvider | "";
  investmentPayment: InvestmentPayment | "";
  visibleToClients: boolean;
}

/** The funnel isn't stored on its own: it's the last chunk of the name. */
const funnelFromName = (name: string): Funnel | "" => {
  const last = name.split("_").pop() ?? "";
  return (FUNNELS as string[]).includes(last) ? (last as Funnel) : "";
};

interface AssociatedCampaignInfoSectionProps {
  briefingId: string;
  associatedId: string;
  associatedCampaign: AssociatedCampaign;
  briefing?: Briefing;
  companyId?: string;
  canEdit: boolean;
  /** Reloads the campaign; resolves once fresh data is in. */
  onSaved: () => Promise<unknown>;
}

const AssociatedCampaignInfoSection: React.FC<
  AssociatedCampaignInfoSectionProps
> = ({
  briefingId,
  associatedId,
  associatedCampaign: ac,
  briefing,
  companyId,
  canEdit,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { role, isClient } = useUserRole();
  const canSetInvestment =
    role === "ADMIN" || role === "ACCOUNT" || role === "PRICING";

  const [updateAssociatedCampaign] = useUpdateAssociatedCampaignMutation();

  const base = useMemo<InfoDraft>(
    () => ({
      platform: ac.platform,
      briefingCampaignType: ac.briefingCampaignType ?? "",
      funnel: funnelFromName(ac.name ?? ""),
      campaignLevel: ac.campaignLevel,
      name: ac.name ?? "",
      subcampaignName: ac.subcampaignName ?? "",
      sourceId: ac.sourceId ?? "",
      advertiserId: ac.advertiserId ?? "",
      nativeAdProvider: ac.nativeAdProvider ?? "",
      investmentPayment: ac.investmentPayment ?? "",
      visibleToClients: ac.visibleToClients ?? true,
    }),
    [ac]
  );

  const handleSave = useCallback(
    async (draft: InfoDraft) => {
      const errors: FieldErrors<InfoDraft> = {};
      if (!draft.name.trim()) {
        errors.name = t(
          "briefingsView.manualNameRequired",
          "Name is required."
        );
      }
      if (draft.platform === "NATADS" && !draft.nativeAdProvider) {
        errors.nativeAdProvider = t(
          "validation.required",
          "This field is required"
        );
      }
      if (Object.keys(errors).length > 0) return errors;

      try {
        await updateAssociatedCampaign({
          briefingId,
          associatedId,
          body: {
            name: draft.name.trim(),
            sourceId: draft.sourceId.trim() || null,
            subcampaignName: draft.subcampaignName.trim() || null,
            briefingCampaignType: draft.briefingCampaignType || undefined,
            platform: draft.platform,
            campaignLevel: draft.campaignLevel,
            advertiserId:
              draft.campaignLevel === "INSERTIONORDER"
                ? draft.advertiserId.trim() || null
                : null,
            nativeAdProvider: draft.nativeAdProvider || undefined,
            investmentPayment: draft.investmentPayment || undefined,
            visibleToClients: draft.visibleToClients,
          },
        }).unwrap();
        await onSaved();
        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.acUpdateSuccess",
              "Campaign updated successfully"
            ),
            severity: "success",
          })
        );
        return true;
      } catch (error) {
        dispatch(
          setToastMessage({
            message:
              extractApiErrorMessage(error) ??
              t("briefingsView.acUpdateError", "Failed to update campaign"),
            severity: "error",
          })
        );
        return false;
      }
    },
    [briefingId, associatedId, updateAssociatedCampaign, onSaved, dispatch, t]
  );

  const section = useInlineSection<InfoDraft>({
    id: "info",
    base,
    onSave: handleSave,
    readOnly: !canEdit,
  });
  const { draft } = section;

  // --- platform mappings: platforms, their campaign types and default level ---
  const { data: mappingsData, isLoading: isLoadingMappings } =
    useGetPlatformMappingsQuery(undefined, { skip: !canEdit });
  const mappings: PlatformMapping[] = useMemo(
    () => mappingsData?.data ?? [],
    [mappingsData]
  );
  const currentMapping = mappings.find((m) => m.platform === draft.platform);

  /** Name = Product_Subcampaign_Campaign_Platform_Type_Funnel. */
  const buildName = useCallback(
    (values: InfoDraft) =>
      [
        briefing?.brandOrProduct,
        values.subcampaignName.trim() || undefined,
        briefing?.campaignName,
        values.platform,
        values.briefingCampaignType || undefined,
        values.funnel || undefined,
      ]
        .filter(Boolean)
        .join("_"),
    [briefing?.brandOrProduct, briefing?.campaignName]
  );

  /** Changes a field that is part of the name, regenerating the name. */
  const setNamePart = (patch: Partial<InfoDraft>) => {
    const next = { ...draft, ...patch };
    section.setFields(briefing ? { ...patch, name: buildName(next) } : patch);
  };

  const revertNamePart = (key: keyof InfoDraft) => () => {
    const next = { ...draft, [key]: base[key] };
    section.cancelField(key);
    if (briefing) section.setField("name", buildName(next));
  };

  const handlePlatformChange = (platform: BriefingPlatform) => {
    const mapping = mappings.find((m) => m.platform === platform);
    // Type, source ID and provider belong to the old platform.
    setNamePart({
      platform,
      briefingCampaignType: "",
      sourceId: "",
      nativeAdProvider: "",
      campaignLevel: mapping?.defaultCampaignLevel ?? "CAMPAIGN",
    });
    if (section.isSoloEditing("platform")) {
      section.startEdit("briefingCampaignType");
      section.startEdit("sourceId");
    }
  };

  const PLATFORM_LINKED: Array<keyof InfoDraft> = [
    "briefingCampaignType",
    "sourceId",
    "nativeAdProvider",
    "campaignLevel",
    "name",
  ];

  // --- source ID picker: candidates of the selected platform ---
  const [
    getPlatformCandidates,
    {
      data: candidatesData,
      isLoading: isLoadingCandidates,
      error: candidatesError,
    },
  ] = useGetPlatformCandidatesMutation();
  // Without a source ID the line can't pull metrics: keep the picker open.
  const sourceMissing = canEdit && !base.sourceId;
  const isEditingSource = section.isEditing("sourceId") || sourceMissing;
  const sourceBinding = bindField(section, "sourceId", canEdit);

  useEffect(() => {
    if (!isEditingSource || !draft.platform) return;
    getPlatformCandidates({
      id: briefingId,
      body: { platformType: draft.platform },
    });
  }, [isEditingSource, draft.platform, briefingId, getPlatformCandidates]);

  useEffect(() => {
    if (!candidatesError) return;
    dispatch(
      setToastMessage({
        message: t(
          "briefingsView.campaignFetchError",
          "Failed to load campaigns from platform. You can still enter the Source ID manually."
        ),
        severity: "error",
      })
    );
  }, [candidatesError, dispatch, t]);

  useEffect(() => {
    const responseErrors = candidatesData?.data?.errors as
      | Array<{ platform?: string; message?: string }>
      | undefined;
    if (!responseErrors || responseErrors.length === 0) return;
    dispatch(
      setToastMessage({
        message: responseErrors
          .map(
            (e) =>
              `${e.platform ? `[${e.platform}] ` : ""}${e.message ?? "Unknown error"}`
          )
          .join("\n"),
        severity: "warning",
      })
    );
  }, [candidatesData, dispatch]);

  // --- labels ---
  const campaignTypeLabel = (type: string) =>
    t(`briefingsCreate.form.briefingCampaignTypeOptions.${type}`, type);
  const levelLabel = (level: string) =>
    level === "INSERTIONORDER"
      ? t("briefingsView.insertionOrder", "Insertion order")
      : level === "LINEITEM"
        ? t("briefingsView.lineItem", "Line item")
        : t("briefingsView.campaign", "Campaign");
  const paymentLabel = (payment: string) =>
    payment === "CLIENTE"
      ? t("briefingsView.paymentClient", "Client")
      : t("briefingsView.paymentTiMediaGroupSl", "TI MEDIA GROUP SL");
  const providerLabel = (provider: string) =>
    provider === "TABOOLA"
      ? t("briefingsView.taboola", "Taboola")
      : t("briefingsView.outbrain", "Outbrain");

  const platformOptions = (
    mappings.length > 0 ? mappings.map((m) => m.platform) : [draft.platform]
  ).map((p) => ({ value: p, label: p }));
  const campaignTypeOptions = (currentMapping?.campaignTypes ?? []).map(
    (type) => ({ value: type, label: campaignTypeLabel(type) })
  );

  const bind = (
    key: keyof InfoDraft,
    editable = canEdit,
    revert?: () => void
  ) => bindField(section, key, editable, revert);

  const platformIcon = resolveBriefingPlatformIcon(draft.platform).iconPath;

  return (
    <SectionCard
      icon={<InfoOutlinedIcon />}
      title={t("briefingsView.campaignInfo", "Campaign info")}
      subtitle={t(
        "briefingsView.campaignInfoSubtitle",
        "Plataforma, identificación y configuración de la línea"
      )}
      dirtyCount={section.dirtyCount}
      saving={section.isSaving}
      onSave={() => void section.save()}
      onDiscard={section.discard}
      createdAt={ac.createdAt}
      updatedAt={ac.updatedAt}
    >
      <Stack spacing={3}>
        <FieldGroup
          title={t("briefingsView.groups.configuration", "Configuración")}
          columns={4}
        >
          <InlineField
            label={t("briefingsView.platform", "Platform")}
            icon={<WebIcon />}
            copyValue={draft.platform}
            value={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  component="img"
                  src={platformIcon}
                  alt=""
                  sx={{ width: 20, height: 20, flexShrink: 0 }}
                />
                <span>{draft.platform}</span>
              </Stack>
            }
            helper={
              section.isEditing("platform") && currentMapping?.realPlatform
                ? `${t("platformMappings.realPlatform", "Real platform")}: ${currentMapping.realPlatform}`
                : undefined
            }
            input={
              <InlineSelect
                value={draft.platform}
                options={platformOptions}
                onChange={(value) =>
                  handlePlatformChange(value as BriefingPlatform)
                }
                disabled={isLoadingMappings}
                autoFocus={section.isSoloEditing("platform")}
              />
            }
            {...bind("platform", canEdit, () =>
              section.cancelField("platform", PLATFORM_LINKED)
            )}
          />
          <InlineField
            label={t(
              "briefingsCreate.form.briefingCampaignType",
              "Campaign type"
            )}
            icon={<CategoryOutlinedIcon />}
            empty={!draft.briefingCampaignType}
            copyValue={
              draft.briefingCampaignType &&
              campaignTypeLabel(draft.briefingCampaignType)
            }
            value={
              draft.briefingCampaignType &&
              campaignTypeLabel(draft.briefingCampaignType)
            }
            input={
              <InlineSelect
                value={draft.briefingCampaignType}
                options={campaignTypeOptions}
                emptyLabel={t("common.none", "None")}
                onChange={(value) =>
                  setNamePart({
                    briefingCampaignType: value as BriefingCampaignType | "",
                  })
                }
                disabled={isLoadingMappings}
              />
            }
            {...bind(
              "briefingCampaignType",
              canEdit,
              revertNamePart("briefingCampaignType")
            )}
          />
          <InlineField
            label={t("briefingsView.level", "Level")}
            icon={<LayersOutlinedIcon />}
            copyValue={levelLabel(draft.campaignLevel)}
            value={levelLabel(draft.campaignLevel)}
            input={
              <InlineSelect
                value={draft.campaignLevel}
                options={["CAMPAIGN", "INSERTIONORDER", "LINEITEM"].map(
                  (level) => ({ value: level, label: levelLabel(level) })
                )}
                onChange={(value) =>
                  section.setField("campaignLevel", value as EntityType)
                }
              />
            }
            {...bind("campaignLevel")}
          />
          <InlineField
            label={t("briefingsView.goalForm.funnel", "Funnel")}
            icon={<FilterAltOutlinedIcon />}
            empty={!draft.funnel}
            copyValue={draft.funnel}
            value={draft.funnel}
            input={
              <InlineSelect
                value={draft.funnel}
                options={FUNNELS.map((f) => ({ value: f, label: f }))}
                onChange={(value) => setNamePart({ funnel: value as Funnel })}
              />
            }
            {...bind("funnel", canEdit, revertNamePart("funnel"))}
          />
          {draft.platform === "NATADS" && (
            <InlineField
              label={t("briefingsView.nativeAdProvider", "Native ad provider")}
              icon={<HubOutlinedIcon />}
              empty={!draft.nativeAdProvider}
              copyValue={draft.nativeAdProvider}
              value={
                draft.nativeAdProvider && providerLabel(draft.nativeAdProvider)
              }
              input={
                <InlineSelect
                  value={draft.nativeAdProvider}
                  options={["TABOOLA", "OUTBRAIN"].map((p) => ({
                    value: p,
                    label: providerLabel(p),
                  }))}
                  onChange={(value) =>
                    section.setField(
                      "nativeAdProvider",
                      value as NativeAdProvider
                    )
                  }
                  error={Boolean(section.errors.nativeAdProvider)}
                />
              }
              {...bind("nativeAdProvider")}
            />
          )}
          <InlineField
            label={t("briefingsView.investmentPayment", "Investment payment")}
            icon={<PaymentsOutlinedIcon />}
            empty={!draft.investmentPayment}
            copyValue={
              draft.investmentPayment && paymentLabel(draft.investmentPayment)
            }
            value={
              draft.investmentPayment && paymentLabel(draft.investmentPayment)
            }
            input={
              <InlineSelect
                value={draft.investmentPayment}
                options={["CLIENTE", "TI_MEDIA_GROUP_SL"].map((p) => ({
                  value: p,
                  label: paymentLabel(p),
                }))}
                emptyLabel={t("common.none", "None")}
                onChange={(value) =>
                  section.setField(
                    "investmentPayment",
                    value as InvestmentPayment | ""
                  )
                }
              />
            }
            {...bind("investmentPayment", canEdit && canSetInvestment)}
          />
          {!isClient && (
            <InlineField
              label={t(
                "briefingsView.visibleToClients",
                "Visible para el cliente"
              )}
              icon={<VisibilityOutlinedIcon />}
              value={
                draft.visibleToClients
                  ? t("common.yes", "Sí")
                  : t("common.no", "No")
              }
              input={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch
                    size="small"
                    color="success"
                    checked={draft.visibleToClients}
                    onChange={(event) =>
                      section.setField("visibleToClients", event.target.checked)
                    }
                  />
                  <Typography variant="body2">
                    {draft.visibleToClients
                      ? t("common.yes", "Sí")
                      : t("common.no", "No")}
                  </Typography>
                </Stack>
              }
              {...bind("visibleToClients")}
            />
          )}
        </FieldGroup>

        <FieldGroup
          title={t("briefingsView.groups.identification", "Identificación")}
          columns={2}
        >
          <InlineField
            span="full"
            label={t("common.name", "Name")}
            icon={<LabelOutlinedIcon />}
            auto
            autoHint={t(
              "briefingsView.nameFormula",
              "Producto_Subcampaña_Campaña_Plataforma_Tipo_Funnel"
            )}
            copyValue={draft.name}
            value={draft.name}
            input={
              <InlineText
                value={draft.name}
                onChange={(event) =>
                  section.setField("name", event.target.value)
                }
                error={Boolean(section.errors.name)}
                autoFocus={section.isSoloEditing("name")}
              />
            }
            {...bind("name")}
          />
          <InlineField
            label={t("briefingsCreate.form.subcampaignName", "Subcampaign")}
            icon={<AccountTreeOutlinedIcon />}
            empty={!draft.subcampaignName}
            copyValue={draft.subcampaignName}
            value={draft.subcampaignName}
            input={
              <InlineText
                value={draft.subcampaignName}
                onChange={(event) =>
                  setNamePart({ subcampaignName: event.target.value })
                }
                placeholder={t(
                  "briefingsView.subcampaignNamePlaceholder",
                  "Optional — leave blank if not applicable"
                )}
                autoFocus={section.isSoloEditing("subcampaignName")}
              />
            }
            {...bind(
              "subcampaignName",
              canEdit,
              revertNamePart("subcampaignName")
            )}
          />
          {draft.campaignLevel === "INSERTIONORDER" && (
            <InlineField
              label={t("briefingsView.advertiserId", "Advertiser ID")}
              icon={<StorefrontOutlinedIcon />}
              mono
              empty={!draft.advertiserId}
              copyValue={draft.advertiserId}
              value={draft.advertiserId}
              helper={
                section.isEditing("advertiserId")
                  ? t(
                      "briefingsView.advertiserIdHelp",
                      "Optional. Defaults to the company's first DV360 advertiser ID."
                    )
                  : undefined
              }
              input={
                <InlineText
                  value={draft.advertiserId}
                  onChange={(event) =>
                    section.setField("advertiserId", event.target.value)
                  }
                  autoFocus={section.isSoloEditing("advertiserId")}
                />
              }
              {...bind("advertiserId")}
            />
          )}
          <InlineField
            span={isEditingSource ? "full" : 1}
            label={t("briefingsView.sourceId", "Source ID")}
            icon={<TagIcon />}
            mono
            empty={!draft.sourceId}
            copyValue={draft.sourceId}
            value={draft.sourceId}
            input={
              <CandidateSelector
                hideLabel
                selectedPlatform={draft.platform}
                defaultCampaignLevel={
                  currentMapping?.defaultCampaignLevel ?? "CAMPAIGN"
                }
                sourceId={draft.sourceId}
                setSourceId={(id) => section.setField("sourceId", id)}
                setCampaignLevel={(level) =>
                  section.setField("campaignLevel", level)
                }
                setAdvertiserId={(id) => section.setField("advertiserId", id)}
                setNativeAdProvider={(provider) =>
                  section.setField("nativeAdProvider", provider ?? "")
                }
                canFetchCampaigns={!!currentMapping && !!companyId}
                isBusy={isLoadingCandidates}
                candidatesError={candidatesError}
                candidatesData={candidatesData?.data}
              />
            }
            {...sourceBinding}
            editing={isEditingSource}
            solo={
              sourceBinding.solo ||
              (sourceMissing && !section.isEditing("sourceId"))
            }
            cancelable={!sourceMissing}
          />
        </FieldGroup>
      </Stack>
    </SectionCard>
  );
};

export default AssociatedCampaignInfoSection;
